import Vue from "vue";
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";

import store from "@/store";
import { getCommunityStatisticByMonth, getPlaceDisplay } from "@/api/placeService";
import { CommunityType } from "@/types/place";

/**
 * 後端排程 app.apscheduler:store_community_state 開始執行的第一個月。
 * 後端沒有「列出可用月份」的 API，所以下拉選單範圍是寫死的。
 * 設寬只是多幾個空月份（無害），設窄會藏起真實資料 —— 寧可設寬。
 */
export const MAP_HISTORY_START_MONTH = "2025-01";

export type MonthStatus = "loading" | "ready" | "empty" | "error";

export interface MonthEntry {
  status: MonthStatus;
  rows: CommunityType[];
}

export interface PinCoords {
  top: number;
  left: number;
}

/* ---------------- 月份運算（整數運算，不用 Date 加減） ---------------- */
// 刻意不用 d.setMonth(d.getMonth()-1) + toISOString()：
// toISOString() 是 UTC，在 UTC+8 的每月頭 8 小時會算錯月；setMonth 在 29~31 號會溢位。

export function monthToIndex(ym: string): number {
  const [y, m] = ym.split("-").map(Number);
  return y * 12 + (m - 1);
}

export function indexToMonth(idx: number): string {
  const y = Math.floor(idx / 12);
  const m = idx % 12 + 1;
  return `${y}-${String(m).padStart(2, "0")}`;
}

export function shiftMonth(ym: string, delta: number): string {
  return indexToMonth(monthToIndex(ym) + delta);
}

/** 顯示月份 M 要查 API 的 M+1：資料記在執行月份底下（與 /maphistory 相同慣例） */
export function toApiMonth(ym: string): string {
  return shiftMonth(ym, 1);
}

/** 可顯示的最新月份 = 上個月 */
export function latestDisplayMonth(): string {
  const now = new Date();
  return indexToMonth(now.getFullYear() * 12 + now.getMonth() - 1);
}

/** 由新到舊的月份清單 */
export function buildMonthList(start: string, end: string): string[] {
  const list: string[] = [];
  for (let i = monthToIndex(end); i >= monthToIndex(start); i--) {
    list.push(indexToMonth(i));
  }
  return list;
}

export function isMonthInRange(ym: string): boolean {
  const idx = monthToIndex(ym);
  return idx >= monthToIndex(MAP_HISTORY_START_MONTH) && idx <= monthToIndex(latestDisplayMonth());
}

/* ---------------- 去重用的 in-flight 紀錄 ---------------- */
// Promise 不可放進 Vuex state（不可序列化、會被 Vue 遍歷）。
// module 檔案本身是單例，元件重掛載後這些紀錄仍然有效。
const inFlight = new Map<string, Promise<void>>();
let posPromise: Promise<void> | null = null;

export interface CountyCommunityMapHistoryState {
  posData: { [fullcode: string]: PinCoords };
  posLoaded: boolean;
  monthCache: { [ym: string]: MonthEntry };
}

@Module({ store, dynamic: true, name: "countyCommunityMapHistoryModule" })
class CountyCommunityMapHistoryModule extends VuexModule
  implements CountyCommunityMapHistoryState {
  public posData: { [fullcode: string]: PinCoords } = {};
  public posLoaded = false;
  // 刻意不設全域 loading 旗標：loading 由「當前月份的 entry.status」推導，
  // 這樣背景預抓完成才不會誤關前景 spinner，慢回應的舊月份也只能寫入自己那個 key。
  public monthCache: { [ym: string]: MonthEntry } = {};

  @Action({ rawError: true }) async ensurePosData(): Promise<void> {
    if (this.posLoaded) return;
    if (posPromise) return posPromise;

    posPromise = (async () => {
      try {
        const res = await getPlaceDisplay();
        const data = res && res.data ? res.data : {};
        this.setPosData(data);
        this.setPosLoaded(true);
      } catch (e) {
        console.error("Failed to fetch position data", e);
      } finally {
        posPromise = null;
      }
    })();

    return posPromise;
  }

  @Action({ rawError: true }) async fetchMonth(payload: { ym: string; silent?: boolean }): Promise<void> {
    const { ym, silent = false } = payload;

    // 已快取（ready / empty / loading）就不重抓；只有 error 允許重試
    const cached = this.monthCache[ym];
    if (cached && cached.status !== "error") return;
    if (inFlight.has(ym)) return inFlight.get(ym);

    // 同步標記 loading：UI 立刻有 spinner，也讓上面那道檢查能擋掉重複呼叫
    this.setMonthEntry({ ym, entry: { status: "loading", rows: [] } });

    const task = (async () => {
      try {
        const raw = await getCommunityStatisticByMonth(toApiMonth(ym), silent);
        // 刻意不合併 *_yellow 進 *_red（communityHistoryModule.getPlaceByDate 會那樣做）：
        // pin 的顏色門檻建立在原始 red 上，合併後每個 pin 都會偏紅且與 /new_map 不一致。
        const rows = Array.isArray(raw)
          ? raw
          : (raw && Array.isArray(raw.data) ? raw.data : null);

        if (rows === null) {
          this.setMonthEntry({ ym, entry: { status: "error", rows: [] } });
        } else if (rows.length === 0) {
          this.setMonthEntry({ ym, entry: { status: "empty", rows: [] } });
        } else {
          this.setMonthEntry({ ym, entry: { status: "ready", rows } });
        }
      } catch (e) {
        console.error(`Failed to fetch community state for ${ym}`, e);
        this.setMonthEntry({ ym, entry: { status: "error", rows: [] } });
      } finally {
        inFlight.delete(ym);
      }
    })();

    inFlight.set(ym, task);
    return task;
  }

  @Action({ rawError: true }) async selectMonth(ym: string): Promise<void> {
    await this.ensurePosData();
    await this.fetchMonth({ ym, silent: false });
    // 背景預抓前後月，不 await：讓當月先渲染出來
    this.prefetchNeighbours(ym);
  }

  @Action({ rawError: true }) async prefetchNeighbours(ym: string): Promise<void> {
    [shiftMonth(ym, -1), shiftMonth(ym, 1)].forEach(n => {
      if (!isMonthInRange(n)) return;
      if (this.monthCache[n]) return;
      if (inFlight.has(n)) return;
      this.fetchMonth({ ym: n, silent: true }).catch(() => undefined);
    });
  }

  // Vue 2：新增 key 一定要用 Vue.set，直接指派不會觸發任何重繪
  @Mutation setMonthEntry(payload: { ym: string; entry: MonthEntry }): void {
    Vue.set(this.monthCache, payload.ym, payload.entry);
  }

  @Mutation setPosData(data: { [fullcode: string]: PinCoords }): void {
    this.posData = data;
  }

  @Mutation setPosLoaded(loaded: boolean): void {
    this.posLoaded = loaded;
  }

  @Mutation clearCache(): void {
    this.monthCache = {};
  }
}

export const countyCommunityMapHistoryModule = getModule(CountyCommunityMapHistoryModule);
